import {PageHeader} from '../ui/PageHeader';
import {ResponsiveTable} from '../ui/ResponsiveTable';
import {useRef,useState} from 'react';
import {api} from '../api';
import {ErrorState} from '../ui/State';
import {DocumentAutomationRate} from '../components/DocumentAutomationRate';

type Group={kind:string;primary:string;secondary:string;waiting:number;wouldFile:number;reasons:Record<string,number>};
type Chunk={scanned:number;nextCursor:string|null;groups:Group[]};
const keyOf=(group:Group)=>[group.kind,group.secondary].join('|');
function merge(into:Map<string,Group>,groups:Group[]){
  for(const group of groups){
    const known=into.get(keyOf(group));
    if(!known){into.set(keyOf(group),{...group,reasons:{...group.reasons}});continue;}
    known.waiting+=group.waiting;known.wouldFile+=group.wouldFile;
    for(const [reason,count] of Object.entries(group.reasons))known.reasons[reason]=(known.reasons[reason]??0)+count;
  }
}
const label=(group:Group)=>`${group.kind.replaceAll('_',' ').toLowerCase()} · ${group.secondary||'unclassified'}`;
const percent=(part:number,whole:number)=>whole?`${Math.round(part*100/whole)}%`:'0%';

/** What stands between each waiting document and automatic filing. Read-only: it files and changes nothing. */
export function FilingReadiness(){
  const [groups,setGroups]=useState<Group[]>(),[scanned,setScanned]=useState(0),[running,setRunning]=useState(false),[finished,setFinished]=useState(false),[error,setError]=useState<unknown>();
  const stopping=useRef(false),active=useRef(false);
  async function run(){
    if(active.current)return;
    active.current=true;stopping.current=false;setRunning(true);setFinished(false);setError(undefined);setGroups([]);setScanned(0);
    const tally=new Map<string,Group>();let after:string|undefined,total=0;
    try{
      do{
        const chunk=await api<Chunk>(`/api/documents/filing-readiness?limit=50${after?`&after=${after}`:''}`);
        merge(tally,chunk.groups);total+=chunk.scanned;setScanned(total);
        setGroups([...tally.values()].sort((a,b)=>b.waiting-a.waiting));
        after=chunk.nextCursor??undefined;
        if(stopping.current)break;
      }while(after);
      setFinished(!after);
    }catch(caught){setError(caught);}
    finally{active.current=false;setRunning(false);}
  }
  const all=groups??[];
  const waiting=all.reduce((sum,group)=>sum+group.waiting,0),ready=all.reduce((sum,group)=>sum+group.wouldFile,0);
  return <div>
    <PageHeader title="Automatic filing readiness" context="For every document waiting for review: would the system file it on its own today, and if not, why not. This only reads. It files nothing and changes nothing."/>
    <DocumentAutomationRate/>
    <div className="actions">
      <button disabled={running} onClick={()=>void run()}>{running?'Checking…':groups?'Check again':'Run the report'}</button>
      {running&&<button className="secondary" onClick={()=>{stopping.current=true;}}>Stop</button>}
    </div>
    {Boolean(error)&&<ErrorState error={error} subject="The readiness report"/>}
    {groups&&<p role="status">{running?`Checked ${scanned} documents so far…`:finished?`Checked all ${scanned} waiting documents.`:`Stopped after ${scanned} documents.`}</p>}
    {groups&&scanned>0&&<>
      <div className="metric-grid">
        <div className="metric"><span>Waiting</span><strong>{waiting}</strong></div>
        <div className="metric"><span>Would be filed now</span><strong>{ready}</strong><small>{percent(ready,waiting)} of those checked</small></div>
      </div>
      <section className="panel" aria-labelledby="readiness-groups">
        <h2 id="readiness-groups">By kind and sub-category</h2>
        <p>The biggest groups come first. A group with many waiting and few filed, held mostly by "This document type needs individual review", is a kind worth adding a rule for.</p>

<ResponsiveTable>
          <thead><tr><th scope="col">Kind and category</th><th scope="col">Waiting</th><th scope="col">Filed now</th><th scope="col">Why the rest are held</th></tr></thead>
          <tbody>{all.map(group=>{
            const reasons=Object.entries(group.reasons).sort((a,b)=>b[1]-a[1]);
            return <tr key={keyOf(group)}>
              <th scope="row">{label(group)}</th><td>{group.waiting}</td><td>{group.wouldFile}</td>
              <td>{reasons.length?<><ul>{reasons.slice(0,3).map(([reason,count])=><li key={reason}>{reason} ({count})</li>)}</ul>
                {reasons.length>3&&<details><summary>{reasons.length-3} more</summary><ul>{reasons.slice(3).map(([reason,count])=><li key={reason}>{reason} ({count})</li>)}</ul></details>}</>:'None'}</td>
            </tr>;})}</tbody>
        </ResponsiveTable>

      </section>
    </>}
  </div>;
}
